import { AutoTradeButton, Button } from '@library/components/buttons';
import {
  BoardTimePicker,
  DisplayTimePicker,
  StartTimePicker
} from '@library/components/simulations';
import { useCallback, useState } from 'react';
import { ActiveSwitch } from '@library/components/modals/PlayerModal/NoContextPlayerModal/StatTable';
import type { BulkAutoTradeChange } from '@bo-hooks/boardBuilder/useBoardBuilderProjections';
import type { DateTime } from 'luxon';
import type { Status } from '@library/types/projection';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { useSplitFlag } from '@bo-hooks/useSplitFlag';

export interface StatTypeHeaderProps {
  allProjectionsActive: boolean;
  allProjectionsAutoTrade: boolean;
  isBulkAutoTradeDisabled: boolean;
  initialProjectionTimes: {
    initialBoardTime: string;
    initialStartTime: string;
    initialDisplayTime: string;
  };
  onBulkAutoTradeChange: ({
    autoTrade,
    statTypeId
  }: BulkAutoTradeChange) => void;
  onBulkTimeChange: ({
    durationId,
    statTypeId,
    boardTime,
    startTime,
    displayTime
  }: {
    durationId?: string;
    statTypeId?: string;
    boardTime?: DateTime;
    startTime?: DateTime;
    displayTime?: DateTime;
  }) => void;
  onToggleDurationStatTypeAllProjectionsStatus: ({
    durationId,
    statTypeId,
    status
  }: {
    durationId: string;
    statTypeId: string;
    status: Status;
  }) => void;
  onToggleShowProjections: () => void;
  showProjections: boolean;
  selectedDurationId: string;
  statTypeId: string;
  statTypeName: string;
  isDisplayTimeVisible: boolean;
}

export const StatTypeHeader = ({
  allProjectionsActive,
  allProjectionsAutoTrade,
  isBulkAutoTradeDisabled,
  initialProjectionTimes,
  onBulkAutoTradeChange,
  onBulkTimeChange,
  onToggleDurationStatTypeAllProjectionsStatus,
  onToggleShowProjections,
  showProjections,
  selectedDurationId,
  statTypeId,
  statTypeName,
  isDisplayTimeVisible
}: StatTypeHeaderProps) => {
  const [bulkBoardTime, setBulkBoardTime] = useState<DateTime>();
  const [bulkStartTime, setBulkStartTime] = useState<DateTime>();
  const [bulkDisplayTime, setBulkDisplayTime] = useState<DateTime>();

  const { isActive: isAutoTraderEnabled } = useSplitFlag({
    flagName: 'AUTO_TRADER_ENABLED'
  });

  const handleAutoTradeChange = () => {
    onBulkAutoTradeChange({
      autoTrade: !allProjectionsAutoTrade,
      durationId: selectedDurationId,
      statTypeId
    });
  };

  const handleBulkTimeChange = useCallback(
    (value: DateTime, key: string) => {
      if (value && key === 'displayTime') {
        setBulkDisplayTime(value);
        onBulkTimeChange({
          durationId: selectedDurationId,
          statTypeId,
          displayTime: value
        });
      }
      if (value && key === 'startTime') {
        setBulkStartTime(value);
        onBulkTimeChange({
          durationId: selectedDurationId,
          statTypeId,
          startTime: value
        });
      }

      if (value && key === 'boardTime') {
        setBulkBoardTime(value);
        onBulkTimeChange({
          durationId: selectedDurationId,
          statTypeId,
          boardTime: value
        });
      }
    },
    [onBulkTimeChange, selectedDurationId, statTypeId]
  );

  return (
    <div className="bg-soFresh-120 border-soFresh-140 font-heebo text-soClean-100 box-border flex w-full flex-row items-center justify-between px-3 text-base font-medium leading-6">
      <span>{statTypeName}</span>
      <div className="flex items-center gap-4">
        {isAutoTraderEnabled && (
          <AutoTradeButton
            autoTrade={allProjectionsAutoTrade}
            disabled={isBulkAutoTradeDisabled}
            onClick={handleAutoTradeChange}
            standalone
          />
        )}
        {initialProjectionTimes.initialBoardTime && (
          <Tooltip text="Change Board Time for all below projections">
            <div className="flex items-center gap-4">
              <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
                Bulk Board Time
              </span>

              <BoardTimePicker
                boardTime={
                  bulkBoardTime?.toISO() ||
                  initialProjectionTimes.initialBoardTime
                }
                handleChange={(boardTime) => {
                  handleBulkTimeChange(boardTime, 'boardTime');
                }}
                initialBoardTime={initialProjectionTimes.initialBoardTime}
                startTime={
                  bulkStartTime?.toISO() ||
                  initialProjectionTimes.initialStartTime
                }
              />
            </div>
          </Tooltip>
        )}
        {initialProjectionTimes.initialStartTime && (
          <Tooltip text="Change Start Time for all below projections">
            <div className="flex items-center gap-4">
              <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
                Bulk Start Time
              </span>

              <StartTimePicker
                handleChange={(startTime) => {
                  handleBulkTimeChange(startTime, 'startTime');
                }}
                initialStartTime={initialProjectionTimes.initialStartTime}
                startTime={
                  bulkStartTime?.toISO() ||
                  initialProjectionTimes.initialStartTime
                }
              />
            </div>
          </Tooltip>
        )}
        {initialProjectionTimes.initialDisplayTime && isDisplayTimeVisible && (
          <Tooltip text="Change Start Time for all below projections">
            <div className="flex items-center gap-4">
              <span className="font-heebo text-soClean-140 text-sm font-medium tracking-wide">
                Bulk Display Time
              </span>

              <DisplayTimePicker
                handleChange={(displayTime) => {
                  handleBulkTimeChange(displayTime, 'displayTime');
                }}
                initialDisplayTime={initialProjectionTimes.initialDisplayTime}
                displayTime={
                  bulkDisplayTime?.toISO() ||
                  initialProjectionTimes.initialDisplayTime
                }
              />
            </div>
          </Tooltip>
        )}
        <ActiveSwitch
          active={allProjectionsActive}
          disabled={false}
          isTableElement={false}
          handleChange={(active: boolean) =>
            onToggleDurationStatTypeAllProjectionsStatus({
              durationId: selectedDurationId,
              statTypeId,
              status: active ? 'pre_game' : 'removed'
            })
          }
          statType={`${selectedDurationId}-${statTypeId}-${statTypeName}`}
          status={allProjectionsActive ? 'pre_game' : 'removed'}
        />
        <Button
          type="button"
          title="ToggleGameTeamPlayersButton"
          onClick={onToggleShowProjections}
        >
          <img
            alt="Chevron Down icon"
            className={classNames(showProjections ? 'rotate-180' : '')}
            src={chevronDownIcon}
          />
        </Button>
      </div>
    </div>
  );
};
